import { Vector2 } from './types';

export enum PowerUpType {
  RAPID_FIRE = 'rapidFire',
  PENETRATING = 'penetrating',
  BOMB = 'bomb',
  SHIELD = 'shield'
}

export interface PowerUpEffect {
  type: PowerUpType;
  duration?: number;
  active: boolean;
  timer?: number;
}

export class PowerUp {
  position: Vector2;
  velocity: Vector2;
  width: number = 30;
  height: number = 30;
  alive: boolean = true;
  type: PowerUpType;
  color: string;
  glowTimer: number = 0;

  constructor(x: number, y: number, type: PowerUpType) {
    this.position = { x, y };
    this.velocity = { x: 0, y: 80 };
    this.type = type;
    this.color = this.getColorByType(type);
  }

  private getColorByType(type: PowerUpType): string {
    switch (type) {
      case PowerUpType.RAPID_FIRE:
        return '#00ff00';  // 緑 - 連射
      case PowerUpType.PENETRATING:
        return '#0088ff';  // 青 - 貫通
      case PowerUpType.BOMB:
        return '#ff0000';  // 赤 - ボム
      case PowerUpType.SHIELD:
        return '#ffff00';  // 黄 - シールド
    }
  }

  update(deltaTime: number): void {
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    this.glowTimer += deltaTime * 4;

    if (this.position.y > 650) {
      this.alive = false;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    
    // グロー効果
    const glowIntensity = 0.7 + 0.3 * Math.sin(this.glowTimer);
    ctx.shadowBlur = 15;
    ctx.shadowColor = this.color;
    ctx.globalAlpha = glowIntensity;
    
    // アイテム本体
    ctx.fillStyle = this.color;
    ctx.fillRect(this.position.x, this.position.y, this.width, this.height);
    
    // アイコン描画
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    
    const centerX = this.position.x + this.width / 2;
    const centerY = this.position.y + this.height / 2 + 5;
    
    switch (this.type) {
      case PowerUpType.RAPID_FIRE:
        ctx.fillText('R', centerX, centerY);
        break;
      case PowerUpType.PENETRATING:
        ctx.fillText('P', centerX, centerY);
        break;
      case PowerUpType.BOMB:
        ctx.fillText('B', centerX, centerY);
        break;
      case PowerUpType.SHIELD:
        ctx.fillText('S', centerX, centerY);
        break;
    }
    
    ctx.restore();
  }
}

export class PowerUpManager {
  private powerUps: PowerUp[] = [];
  private effects: Map<PowerUpType, PowerUpEffect> = new Map();
  private nextDropScore: number = 1000;
  private scoreDropInterval: number = 1500;

  constructor() {
    this.initializeEffects();
  }

  private initializeEffects(): void {
    this.effects.set(PowerUpType.RAPID_FIRE, {
      type: PowerUpType.RAPID_FIRE,
      duration: 10000,
      active: false,
      timer: 0
    });
    
    this.effects.set(PowerUpType.PENETRATING, {
      type: PowerUpType.PENETRATING,
      duration: 8000,
      active: false,
      timer: 0
    });
    
    this.effects.set(PowerUpType.BOMB, {
      type: PowerUpType.BOMB,
      active: false
    });
    
    this.effects.set(PowerUpType.SHIELD, {
      type: PowerUpType.SHIELD,
      duration: 5000,
      active: false,
      timer: 0
    });
  }

  update(deltaTime: number, currentScore: number): void {
    // アイテムドロップ判定
    if (currentScore >= this.nextDropScore) {
      this.dropRandomPowerUp();
      this.nextDropScore += this.scoreDropInterval;
    }

    // アイテム更新
    this.powerUps.forEach(powerUp => powerUp.update(deltaTime));
    this.powerUps = this.powerUps.filter(powerUp => powerUp.alive);

    // 効果の更新
    this.effects.forEach(effect => {
      if (effect.active && effect.duration && effect.timer !== undefined) {
        effect.timer += deltaTime * 1000;
        if (effect.timer >= effect.duration) {
          effect.active = false;
          effect.timer = 0;
        }
      }
    });
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.powerUps.forEach(powerUp => powerUp.render(ctx));
    this.renderActiveEffects(ctx);
  }

  private renderActiveEffects(ctx: CanvasRenderingContext2D): void {
    let yOffset = 150;
    
    this.effects.forEach(effect => {
      if (effect.active) {
        ctx.save();
        // ボムの場合は赤色で表示
        ctx.fillStyle = effect.type === PowerUpType.BOMB ? '#ff0000' : '#ffffff';
        ctx.font = '16px Arial';
        
        let text = '';
        switch (effect.type) {
          case PowerUpType.RAPID_FIRE:
            text = 'RAPID FIRE';
            break;
          case PowerUpType.PENETRATING:
            text = 'PENETRATING';
            break;
          case PowerUpType.BOMB:
            text = 'BOMB - Press X';
            break;
          case PowerUpType.SHIELD:
            text = 'SHIELD';
            break;
        }
        
        if (effect.duration && effect.timer !== undefined) {
          const remaining = Math.ceil((effect.duration - effect.timer) / 1000);
          text += ` ${remaining}s`;
        }
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.strokeText(text, 20, yOffset);
        ctx.fillText(text, 20, yOffset);
        
        yOffset += 25;
        ctx.restore();
      }
    });
  }

  private dropRandomPowerUp(): void {
    const types = Object.values(PowerUpType);
    const randomType = types[Math.floor(Math.random() * types.length)];
    const x = 50 + Math.random() * 700;
    this.powerUps.push(new PowerUp(x, -30, randomType));
  }

  checkCollision(playerX: number, playerY: number, playerWidth: number, playerHeight: number): PowerUpType | null {
    for (const powerUp of this.powerUps) {
      if (playerX < powerUp.position.x + powerUp.width &&
          playerX + playerWidth > powerUp.position.x &&
          playerY < powerUp.position.y + powerUp.height &&
          playerY + playerHeight > powerUp.position.y) {
        
        powerUp.alive = false;
        return powerUp.type;
      }
    }
    return null;
  }

  activatePowerUp(type: PowerUpType): void {
    const effect = this.effects.get(type);
    if (effect) {
      effect.active = true;
      effect.timer = 0;
    }
  }

  isEffectActive(type: PowerUpType): boolean {
    const effect = this.effects.get(type);
    return effect ? effect.active : false;
  }

  getBombCount(): number {
    const bombEffect = this.effects.get(PowerUpType.BOMB);
    return bombEffect?.active ? 1 : 0;
  }

  useBomb(): boolean {
    const bombEffect = this.effects.get(PowerUpType.BOMB);
    if (bombEffect?.active) {
      bombEffect.active = false;
      return true;
    }
    return false;
  }

  clear(): void {
    this.powerUps = [];
    this.nextDropScore = 1000;
    this.effects.forEach(effect => {
      effect.active = false;
      effect.timer = 0;
    });
  }
}